<template>
  <div>
    <v-container >
      <CRUDCriteria
        :showSearch="false"
        :showReset="true"
      >
        <template v-slot:criteriaRows>
          <v-row>
            <v-col cols="2">
              <v-select
                id="tmc-fund-criteria-fy"
                label="By FY"
                :items="sortedFiscalYears"
                v-model="criteriaState.fiscalYear"
                @change="searchByCriteria"
              ></v-select>
            </v-col>
            <v-col cols="2">
              <v-select
                id="tmc-fund-criteria-type"
                label="By Fund Type"
                :items="fundType"
                item-text="description"
                item-value="name"
                v-model="criteriaState.type"
                @change="searchByCriteria"
              ></v-select>
            </v-col>
            <v-col cols="5">
              <v-btn
                v-if="isTmAdmin"
                class="ml-3"
                color="primary"
                small
                dense
                @click="validateRecords"
              >
                Validate Records
              </v-btn>
              <v-btn
                class="ml-3"
                color="primary"
                small
                dense
                @click="printFundList"
              >
                PRINT FUND LIST
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </CRUDCriteria>
      <CRUDList
        ref="crudList"
        :canAdd="canAddToSharedList"
        :canEdit="true"
        :canSave="canEditSharedList"
        :showSelect="true"
        :baseCrudKey="baseCrudKey"
        :headers="headers"
        :createNewItem="newFundDialog"
        :defaultSortBy="sortBy"
        :selectedItems="selectedItems"
        toolbarTitle="Funds"
        @loadCRUDList="handleLoadCRUDList"
        @upsertItem="upsertItem"
        @emitSelectedItem="emitSelectedItem"
        :loadingData="gridLoading.flag"
      :loadingText="gridLoading.text"
      >
      <template v-slot:[`additionalToolsAtRight`]="{}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              id="sharedFundListRenumberLinesBtn"
              class="shared-fund-list-renumber-lines-btn"
              color="primary"
              fab icon
              :disabled="(selectedItems.length) !== 1"
              @click="renumberLines"
            >
              <v-icon large>
                {{ icons.mdiCounter }}
              </v-icon>
            </v-btn>
          </template>
          <span>Renumber Lines</span>
        </v-tooltip>
        <v-divider
          class="mx-4" inset vertical
        ></v-divider>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              id="sharedFundLineNumberLinesBtn"
              class="shared-fund-line-Number-lines-btn"
              color="primary"
              fab icon
              @click="autoFillLines"
            >
              <v-icon large>
                {{ icons.mdiFormatListNumbered }}
              </v-icon>
            </v-btn>
          </template>
          <span>Auto fill Line number</span>
        </v-tooltip>
      </template>
        <template v-slot:[`editedItem`]="{}">
          <FundEditedItem :baseCrudKey="baseCrudKey"
          @fundEmitData="handleEmitFromFundEditedItem" />
        </template>
        <template v-slot:[`additionalActions`]="{}">
          <span v-if="!isNewItem() && isSuperAdmin">
            <v-btn :class="`input-text-size`"
              text color="blue darken-1"
              @click="deleteFund"
              >Delete</v-btn>
          </span>
      </template>
      </CRUDList>
      <!-- Validation Error Dialog -->
      <v-dialog v-model="isRequiredToShowErrorDialog" max-width="600px" persistent>
        <v-card>
          <v-card-text class="mt-2">
            <v-alert type="error" outlined class="mb-2 mt-2">
              <strong>{{'Validation Errors'}}</strong>
            </v-alert>
            <div v-if="currentValidationError">
              <h4 class="mb-3">
                Record Details ({{ currentErrorIndex + 1 }} of {{ validationErrorData.length }}):
              </h4>
              <v-simple-table dense>
                <tbody>
                  <tr v-if="currentValidationError">
                    <td class="error--text"
                      v-html="Array.isArray(currentValidationError)
                        ? currentValidationError.join('<br>')
                        : (typeof currentValidationError === 'string'
                          ? currentValidationError.replace(/\n/g, '<br>')
                          : currentValidationError)"></td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="hasMoreErrors"
              color="primary"
              text
              @click="showNextError"
            >
              <v-icon left>mdi-arrow-right</v-icon>
              Continue ({{ currentErrorIndex + 1 }}/{{ validationErrorData.length }})
            </v-btn>
            <v-btn
              v-else
              color="primary"
              text
              disabled
            >
              <v-icon left>mdi-check</v-icon>
              Continue
            </v-btn>
            <v-btn color="error" text @click="hideErrorWindow">
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import {
  mapGetters,
  mapActions,
  mapState,
  mapMutations,
} from 'vuex';
import { mdiCounter, mdiFormatListNumbered } from '@mdi/js';
import CRUDList from '../../common/base/crud/CRUDList.vue';
import CRUDCriteria from '../../common/base/crud/CRUDCriteria.vue';
import FundEditedItem from './FundEditedItem.vue';
import {
  toFiscalYear,
  getFYList,
} from '../../../util/shared/tmc-global';
import { isAuthorised } from '../../../store/auth/token';
import { authRoles, TM_ADMIN_JWT_ROLES } from '../../../router/roleGroups';
import { amountToNumber, defaultGridLoading, manageGridLoading } from '../../../util/shared/vue-global';
import SharedListAccessControlMixin from '../SharedListAccessControl.mixin';
import { clients } from '../../../util/clients';

const { backendRest } = clients.direct;

const getCharacters = (val, num) => {
  const getSubString = (val || '').substring(0, num);
  return getSubString;
};

const getFundTypeAbbreviation = (val) => {
  if (val !== 'COLLECTIONFUND') {
    return getCharacters(val, 1);
  }
  return getCharacters('X', 1);
};

const getHeaders = () => {
  const headers = [
    {
      text: 'Type',
      value: 'type',
      formatter: 'customMethod',
      methodDefinition: getFundTypeAbbreviation,
    },
    { text: 'Fund', value: 'fund' },
    { text: 'Description', value: 'description' },
    { text: 'Bank', value: 'bankName' },
    { text: 'Line', value: 'line' },
    { text: 'LastWarrant', value: 'lastWarrant' },
    { text: 'LastPayment', value: 'lastPayment' },
    { text: 'Alt Key', value: 'altKey' },
  ];
  return headers;
};

const getHeadersForPrint = () => [
  { text: 'Fund', value: 'fund' },
  { text: 'Description', value: 'description' },
  { text: 'Bank', value: 'bankName' },
  { text: 'Line', value: 'line' },

];

const bankCrudKey = 'sharedBank';

const extractBankName = (item) => item && item.bank && item.bank.bankName;

const extractBankID = (item) => {
  if (item == null) {
    return null;
  }
  if (typeof item.bank === 'string') {
    return item.bank;
  }
  return item.bank && item.bank._id;
};

export default {
  name: 'FundList',
  mixins: [SharedListAccessControlMixin],
  components: {
    CRUDCriteria,
    CRUDList,
    FundEditedItem,
  },
  data: () => ({
    baseCrudKey: 'sharedFund',
    gridLoading: defaultGridLoading(),
    sortBy: [{
      column: 'fund',
      direction: 'asc',
    },
    ],
    icons: {
      mdiCounter,
      mdiFormatListNumbered,
    },
    isRequiredToShowErrorDialog: false,
    validationErrorData: [],
    currentErrorIndex: 0,
  }),
  created() {
    // reset criteria while merge flag is true
    this.criteriaState = {};
    this.initCriteria();
    this.loadFiscalYears();
  },
  computed: {
    ...mapGetters('auth/token', ['authUserRoles']),
    // TmcSupport or restore-DB roles; unlike the comment dialogs' isTmAdmin (TmcSupport only).
    isTmAdmin() {
      return (this.authUserRoles || []).some((r) => TM_ADMIN_JWT_ROLES.includes(r));
    },
    ...mapGetters('base/crud', [
      'criteria',
      'item',
      'items',
      'index',
    ]),
    ...mapGetters([
      'todaysDate',
    ]),
    ...mapState({
      storedSelectedItems: (state) => state.shared.fund.selectedItems,
      headers: (state) => {
        if (state.isPrintScreen) {
          return getHeadersForPrint();
        }
        return getHeaders();
      },
      fundType: (state) => {
        const fundTypeVals = state.enums.SharedFundTypeEnum || [];
        return [{ name: null, description: '' }, ...fundTypeVals];
      },
      getFiscalYears: (state) => {
        const regYears = state.shared.fund.regYears || {};
        return getFYList(regYears);
      },
      glSettingItem: (state) => state.SystemConfig.glSettingItem || {},
    }),
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    editedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    banks() {
      return this.items(bankCrudKey) || [];
    },
    sortedFiscalYears() {
      return [...(this.getFiscalYears || [])].sort((a, b) => b - a);
    },
    selectedItems: {
      get() {
        return this.storedSelectedItems;
      },
      set(value) {
        this.setSelectedItems(value);
      },
    },
    isSuperAdmin() {
      return isAuthorised([authRoles.SuperAdmin, 'GLAdministration']);
    },
    currentValidationError() {
      const hasErrors = this.validationErrorData.length > 0;
      const validIndex = this.currentErrorIndex < this.validationErrorData.length;
      if (hasErrors && validIndex) {
        return this.validationErrorData[this.currentErrorIndex];
      }
      return this.validationErrorData;
    },
    hasMoreErrors() {
      return this.currentErrorIndex < this.validationErrorData.length - 1;
    },
  },
  methods: {
    manageGridLoading,
    ...mapActions(['reAuth', 'flashInfo', 'flashSuccess']),
    ...mapActions('shared/fund', [
      'loadFunds',
      'upsertFund',
      'deleteSharedFunds',
      'loadFiscalYears',
      'validateSharedFund',
    ]),
    ...mapMutations('base/crud', [
      'setCriteria',
    ]),
    ...mapMutations('shared/fund', [
      'setSelectedItems',
    ]),
    ...mapActions('common/renumberLines', [
      'cmnRenumberLines',
      'cmnAutonumberLines',
    ]),
    ...mapActions('SystemConfig', ['loadConfig',
    ]),
    handleEmitFromFundEditedItem() {
      this.$refs.crudList.$refs.crudDialog.validateForm();
    },
    async fetchConfig() {
      await this.loadConfig({ requiredScope: 'glConfig' });
    },
    initCriteria() {
      this.fetchConfig().then(() => {
        this.criteriaState.fiscalYear = toFiscalYear(new Date(this.todaysDate));
        this.handleLoadCRUDList();
      });
    },
    handleLoadCRUDList() {
      const { baseCrudKey, sortBy } = this;
      const criteria = this.criteria(baseCrudKey);
      manageGridLoading(this, true, 'Searching...');
      const autoRenumber = this.glSettingItem.autoRenumber || false;
      const sortValue = autoRenumber ? [{ column: 'line', direction: 'asc' }] : sortBy;
      this.loadFunds({
        criteria: {
          ...criteria,
          sortBy: sortValue,
        },
        baseCrudKey,
        postProccessFunds: (dataItems) => dataItems.map((dataItem) => ({
          ...dataItem,
          bankName: extractBankName(dataItem),
        })),
      }).then(() => {
        this.setDefaultSelectedItems();
      }).finally(() => {
        manageGridLoading(this, false);
      });
    },
    newFundDialog() {
      let newItem = {};
      newItem = {
        fiscalYear: toFiscalYear(new Date(this.todaysDate)),
        lastWarrant: 0,
        lastPayment: 0,
        enableReconciliation: false,
        isPrintEON: false,
      };
      if (this.criteriaState.fiscalYear && this.criteriaState.fiscalYear.length > 0) {
        newItem.fiscalYear = this.criteriaState.fiscalYear;
      }
      return newItem;
    },
    searchByCriteria() {
      this.handleLoadCRUDList();
    },
    isNewItem() {
      return this.index(this.baseCrudKey) === -1;
    },
    async upsertItem(itemData) {
      const { baseCrudKey } = this;
      const item = this.saveableEdits(itemData);
      if (!this.isNewItem()) {
        if (!window.confirm('This will affect to all associated Transactions, are you sure!')) {
          return false;
        }
      }
      await this.upsertFund({
        item,
        baseCrudKey,
        postProccessFunds: (dataItems) => dataItems.map((dataItem) => ({
          ...dataItem,
          bankName: extractBankName(dataItem),
        })),
      });
      this.setDefaultSelectedItems();
      return true;
    },
    saveableEdits(item) {
      const updatedItem = { ...item };
      updatedItem.bank = extractBankID(item);
      updatedItem.investedAmountInterestCalculations = amountToNumber(
        updatedItem.investedAmountInterestCalculations,
      );
      delete updatedItem.bankName;
      delete updatedItem.lastWarrant;
      delete updatedItem.lastPayment;
      return updatedItem;
    },
    setDefaultSelectedItems() {
      this.$nextTick(() => {
        this.selectedItems = [];
      });
    },
    emitSelectedItem(item) {
      this.selectedItems = item;
    },
    async renumberLines() {
      if (window.confirm('Are you sure you want to renumber lines?')) {
        if (this.selectedItems[0].line !== null) {
          await this.cmnRenumberLines({
            criteria: {
              line: this.selectedItems[0].line,
              fiscalYear: this.selectedItems[0].fiscalYear,
            },
            baseCrudKey: this.baseCrudKey,
          });
          this.handleLoadCRUDList();
          this.flashSuccess(`All the line numbers are now successfully renumbered, for FY ${this.criteriaState.fiscalYear}!`);
        } else {
          this.setDefaultSelectedItems();
          this.flashInfo('The selected transaction does not contain line number!');
        }
      }
      this.setDefaultSelectedItems();
    },
    async autoFillLines() {
      if (window.confirm('Are you sure you want to auto fill lines?')) {
        await this.cmnAutonumberLines({
          criteria: {
            fiscalYear: this.criteriaState.fiscalYear,
          },
          baseCrudKey: this.baseCrudKey,
        });
        this.handleLoadCRUDList();
        this.flashSuccess(`All the line numbers are now successfully auto assigned, for FY ${this.criteriaState.fiscalYear}!`);
      } else {
        this.setDefaultSelectedItems();
      }
      this.setDefaultSelectedItems();
    },
    async deleteFund() {
      const itemVal = this.item(this.baseCrudKey);
      if (window.confirm(`Are you sure you want to delete this Fund ${itemVal.fund}?`)) {
        const { baseCrudKey } = this;
        const deleteSharedFundObj = {
          fiscalYear: itemVal.fiscalYear,
          _id: itemVal._id,
        };
        await this.deleteSharedFunds({ item: deleteSharedFundObj, baseCrudKey }).then(() => {
          this.setDefaultSelectedItems();
        }).catch((e) => {
          console.warn(e);
          this.setDefaultSelectedItems();
        });
      }
    },
    // validateRecords() {
    //   // Implement the validation logic here
    //   // For now, let's assume we have some validation errors
    //   this.validationErrorData = [
    //     'Error 1: Invalid data in field X',
    //     'Error 2: Missing value in field Y',
    //   ];
    //   this.currentErrorIndex = 0;
    //   this.isRequiredToShowErrorDialog = true;
    // },
    showNextError() {
      if (this.hasMoreErrors) {
        this.currentErrorIndex += 1;
      }
    },
    hideErrorWindow() {
      this.currentErrorIndex = 0;
      this.validationErrorData = [];
      this.isRequiredToShowErrorDialog = false;
    },
    async validateRecords() {
      if (this.isFiltersAvailable()) {
        if (window.confirm('Are you sure you want to validate these items?')) {
          const initialCriteria = {
            fiscalYear: this.criteriaState.fiscalYear,
          };
          const validationResult = await this.validateSharedFund({
            criteria: initialCriteria,
          });
          if (validationResult && validationResult.isValid) {
            this.validationErrorData = validationResult.messages || [];
            this.isRequiredToShowErrorDialog = true;
          } else {
            this.flashSuccess('All records are valid!');
          }
          this.handleLoadCRUDList();
        }
      } else {
        this.flashInfo('Please select Fiscal year!');
      }
    },
    isFiltersAvailable() {
      return (this.criteriaState.fiscalYear);
    },
    async printFundList() {
      const { jwt } = await this.reAuth();
      const criteria = { ...this.criteriaState };

      const params = [];
      if (criteria.fiscalYear) {
        params.push(`fiscalYear=${criteria.fiscalYear}`);
      }
      if (criteria.type) {
        const typeMapping = {
          BUDGETEDFUND: 'b',
          CASHFUND: 'c',
          COLLECTIONFUND: 'x',
          ODFUND: 'o',
        };
        const typeValue = typeMapping[criteria.type] || criteria.type;
        params.push(`type=${typeValue}`);
      }
      params.push(`token=${jwt}`);

      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const url = `${backendRest.defaults.baseURL}/shared/fund-list-report${queryString}`;
      window.open(url, '_blank');
    },
  },
};

</script>
